import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import * as api from "./api";

/**
 * The offer in context, and the whole of the Workstation's own state.
 *
 * A2 — ONE WORKSTATION, WHOSE CONTENT FOLLOWS THE SELECTED OBJECT
 * ==============================================================
 * Q4 says the ABH Campaign Workstation is "the same shell"; §4 is written
 * throughout in terms of *the* Offer Workstation. The reading the shell was
 * built for is one Workstation area whose content is determined by the selected
 * offer, which keeps the Y-axis at exactly five entries.
 *
 * WHY `useLocation` AND STATE, RATHER THAN `useSearchParams`
 * =========================================================
 * An area must compose inside the shell using ONLY what the shell provides.
 * `useLocation` is what `CipherShell` itself uses and is therefore part of the
 * surface every area can rely on; `useSearchParams` is not, and requiring it
 * made this area fail to render inside the shell's own tests — correctly, by
 * exposing a dependency the shell had never agreed to.
 *
 * So the URL is the INITIAL value and component state is the working one:
 * `/v2/workstation?offer=off_1` still opens on that offer, which is what makes
 * a link to a colleague work.
 *
 * THE SELECTION NOW WRITES BACK (previously it did not)
 * =====================================================
 * Reading `?offer=` on load but never writing it meant every copied link was
 * stale the moment the operator switched offers — the URL still named whatever
 * they had opened on, so "here, look at this" sent a colleague to the wrong
 * offer while looking correct to both of them. That is the worst kind of broken
 * link: one that resolves.
 *
 * `history.pushState` rather than the router's own navigation, for the reason
 * the section above gives — an area composes with what the shell provides, and
 * the shell provides `useLocation`, not a navigator. pushState changes the URL
 * without a reload and without adding a dependency this area is not allowed to
 * have.
 *
 * Because pushState does not tell the router anything, back and forward are
 * wired explicitly through `popstate`. Without that the selection and the URL
 * diverge the first time the operator presses back, which is a subtler version
 * of the bug being fixed.
 */

/** Where the browser actually is, as opposed to what the router believes. */
function offerFromWindow() {
  try {
    return new URLSearchParams(window.location.search).get("offer") || "";
  } catch (e) {
    return "";
  }
}

/**
 * Put `offerId` in the URL without reloading.
 *
 * No-ops when the value is already there, so re-selecting the open offer does
 * not stack duplicate history entries the operator then has to press back
 * through.
 */
export function writeOfferToUrl(offerId) {
  if (typeof window === "undefined" || !window.history?.pushState) return;
  const next = offerId || "";
  if (next === offerFromWindow()) return;

  const url = new URL(window.location.href);
  if (next) url.searchParams.set("offer", next);
  else url.searchParams.delete("offer");

  window.history.pushState({ offer: next }, "", url.toString());
}
function offerFromLocation(location) {
  // `search` is read defensively: the shell hands areas a location, and not
  // every consumer of this component tree supplies every field of one.
  const search = location?.search || "";
  try {
    return new URLSearchParams(search).get("offer") || "";
  } catch (e) {
    return "";
  }
}

export function useOfferSelection() {
  const location = useLocation();
  const initial = offerFromLocation(location);

  const [selectedId, setSelectedId] = useState(initial);
  const [offers, setOffers] = useState(null);   // null = not loaded yet
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api.listOffers().then((res) => {
      if (!live) return;
      if (!res.ok) {
        setError(res.error);
        setOffers([]);
        return;
      }
      setOffers(Array.isArray(res.data) ? res.data : []);
    });
    return () => {
      live = false;
    };
  }, []);

  const select = useCallback((offerId) => {
    const next = offerId || "";
    setSelectedId(next);
    writeOfferToUrl(next);
  }, []);

  // Back and forward. pushState is invisible to the router, so the URL is the
  // authority here and the selection follows it.
  useEffect(() => {
    const onPop = () => setSelectedId(offerFromWindow());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const selected =
    (offers || []).find((o) => o.offer_id === selectedId) || null;

  return { offers, selected, selectedId, select, error };
}

/**
 * One endpoint, fetched when its inputs change. `{ state, data, error }`.
 *
 * `state` is one of "idle" | "loading" | "ready" | "error", and every panel
 * renders all four rather than assuming the happy path — an endpoint that is
 * founder-gated, slow or empty is the ordinary case here, not the exception.
 */
export function useEndpoint(fn, deps, { skip = false } = {}) {
  const [result, setResult] = useState({
    state: skip ? "idle" : "loading",
    data: null,
    error: null,
    status: null,
  });

  useEffect(() => {
    if (skip) {
      setResult({ state: "idle", data: null, error: null, status: null });
      return undefined;
    }
    let live = true;
    setResult({ state: "loading", data: null, error: null, status: null });
    fn().then((res) => {
      if (!live) return;
      setResult(
        res.ok
          ? { state: "ready", data: res.data, error: null, status: null }
          : {
              state: "error",
              data: null,
              error: res.error,
              status: res.status ?? null,
            }
      );
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, skip]);

  return result;
}

// ── ?session= — the same convention as ?offer= (V2-CREATIVE-WAVE1) ──────────
//
// Ratified by the Founder on 2026-09-08. A creative session needs to be
// identified in the URL so it can be linked, reloaded and navigated back out
// of — and the shell has no route below the tab to put it in: `CipherShell.jsx`
// takes the tab as `split("/")[0]` of the path under the area and DISCARDS the
// rest, while `areas.js` locks the Y-axis at five so there is no Creative area
// to add. The query string is where this shell already carries live objects,
// so a session goes where the offer goes.
//
// Deliberately a separate reader from the offer's rather than a generalised
// one: the offer selection also owns fetching the offer list, and a session has
// no equivalent. Generalising would drag that along.

function sessionFromWindow() {
  if (typeof window === "undefined") return "";
  try {
    return new URLSearchParams(window.location.search).get("session") || "";
  } catch (e) {
    return "";
  }
}

/**
 * Put `sessionId` in the URL without reloading. No-ops when it is already
 * there, so re-opening the session you are in does not stack history entries.
 */
export function writeSessionToUrl(sessionId) {
  if (typeof window === "undefined" || !window.history?.pushState) return;
  const next = sessionId || "";
  if (next === sessionFromWindow()) return;

  const url = new URL(window.location.href);
  if (next) url.searchParams.set("session", next);
  else url.searchParams.delete("session");

  window.history.pushState({ session: next }, "", url.toString());
}

/**
 * The open session, from the URL. `{ sessionId, open, close }`.
 *
 * `close()` clears it, which is how the operator gets back to the list — and
 * back is also a real browser Back, because this uses pushState the same way
 * the offer does.
 */
export function useSessionSelection() {
  const location = useLocation();
  const initial = (() => {
    const search = location?.search || "";
    try {
      return new URLSearchParams(search).get("session") || "";
    } catch (e) {
      return "";
    }
  })();

  const [sessionId, setSessionId] = useState(initial);

  const open = useCallback((id) => {
    const next = id || "";
    setSessionId(next);
    writeSessionToUrl(next);
  }, []);

  const close = useCallback(() => {
    setSessionId("");
    writeSessionToUrl("");
  }, []);

  /* A SESSION IN THE LIBRARY COULD NOT BE CLICKED INTO, and this is why.
     2026-09-14, Founder's report.

     The URL was read ONCE, into `useState(initial)`. `CreativeActions`
     navigates with React Router's `navigate()`, which uses `pushState` — and
     the only resync below is a `popstate` listener, which `pushState` does not
     fire. So the URL changed to `…&session=cs_…` and the screen stayed on the
     list. A full page load on the same URL worked, which is the tell.

     The router's own `location` is the one thing that DOES update on
     `navigate()`, so the selection follows it. `popstate` is kept for the
     back/forward case, where the router may not re-render but the URL changes;
     both write into the same state and agree, because both read the URL. */
  useEffect(() => {
    const fromUrl = (() => {
      try {
        return new URLSearchParams(location?.search || "").get("session") || "";
      } catch (e) {
        return "";
      }
    })();
    setSessionId((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [location?.search]);

  // pushState is invisible to the router, so the URL is the authority and the
  // selection follows it — same reason the offer selection does this.
  useEffect(() => {
    const onPop = () => setSessionId(sessionFromWindow());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return { sessionId, open, close };
}
